import { useState } from "react";
import { Panel, ResourceTable, SelectField, SimpleForm, TextField } from "./ui.jsx";

export function canCreateCustomerWorkspaces(user, overview) {
  return user?.role === "admin" && user?.platformOperator === true;
}

export function shouldShowTrialWorkspaceAccessNotice(user, overview) {
  return user?.role === "admin" && overview?.workspace?.id === "demo" && user?.platformOperator !== true;
}

export function TrialWorkspaceAccessNotice() {
  return (
    <Panel title="Trial Workspace Launcher" subtitle="Create customer trial login pages from the platform operator account.">
      <div className="notice">
        This admin can onboard employees and manage teams inside the current workspace. Customer trial workspace creation is reserved for the platform operator account.
      </div>
    </Panel>
  );
}

export function AdminLaunchSection({ workspaces, onCreate }) {
  const [workspaceForm, setWorkspaceForm] = useState(defaultWorkspaceForm);
  const [resetForm, setResetForm] = useState({ workspaceId: "", username: "", newPassword: "" });
  const [resetMessage, setResetMessage] = useState("");
  async function resetPassword() {
    setResetMessage("");
    if (!resetForm.workspaceId) throw new Error("Select a trial workspace.");
    const saved = await onCreate(`/workspaces/${resetForm.workspaceId}/reset-password`, { username: resetForm.username, newPassword: resetForm.newPassword }, { throwOnError: true });
    if (saved === false) throw new Error("Password reset failed");
    setResetForm((current) => ({ ...current, newPassword: "" }));
    setResetMessage("Password reset. Share the temporary password privately. The user must change it at next sign-in; previous sessions have been revoked.");
  }

  async function createWorkspace() {
    const saved = await onCreate("/workspaces", {
      ...workspaceForm,
      userLimit: Number(workspaceForm.userLimit || 0),
      teamLimit: Number(workspaceForm.teamLimit || 0),
    }, { throwOnError: true });
    if (saved === false) throw new Error("Save failed");
    setWorkspaceForm(defaultWorkspaceForm());
  }

  function updateWorkspaceForm(patch) {
    setWorkspaceForm((current) => ({ ...current, ...patch }));
  }

  return (
    <section className="admin-launch-grid">
      <Panel title="Create Trial Customer Workspace" subtitle="Give every trial customer a private login page while the demo remains available at the home page.">
        <SimpleForm submitLabel="Create Trial Workspace" submittingLabel="Creating..." onSubmit={createWorkspace}>
          <TextField
            label="Customer company name"
            value={workspaceForm.companyName}
            required
            maxLength={140}
            onChange={(companyName) =>
              updateWorkspaceForm({
                companyName,
                slug: workspaceForm.slug ? workspaceForm.slug : slugifyWorkspace(companyName),
              })
            }
          />
          <TextField
            label="Workspace URL slug"
            value={workspaceForm.slug}
            required
            maxLength={60}
            pattern={"[a-z0-9\\-]+"}
            onChange={(slug) => updateWorkspaceForm({ slug: slugifyWorkspace(slug) })}
          />
          <TextField label="Customer admin name" value={workspaceForm.adminName} required maxLength={140} onChange={(adminName) => updateWorkspaceForm({ adminName })} />
          <TextField label="Customer admin email" type="email" value={workspaceForm.adminEmail} required maxLength={180} onChange={(adminEmail) => updateWorkspaceForm({ adminEmail })} />
          <TextField label="Trial end date" type="date" value={workspaceForm.trialEndDate} onChange={(trialEndDate) => updateWorkspaceForm({ trialEndDate })} />
          <SelectField
            label="Sign-in mode"
            value={workspaceForm.loginMode}
            options={[
              { value: "password", label: "Password sign-in (recommended for real clients)" },
              { value: "ldap", label: "Enterprise sign-in (LDAP configuration required)" },
            ]}
            onChange={(loginMode) => updateWorkspaceForm({ loginMode })}
          />
          {workspaceForm.loginMode === "password" ? (
            <TextField
              label="Customer admin temporary password"
              type="password"
              value={workspaceForm.adminPassword}
              required
              maxLength={128}
              onChange={(adminPassword) => updateWorkspaceForm({ adminPassword })}
            />
          ) : null}
          <TextField label="User limit" type="number" min="1" max="500" value={workspaceForm.userLimit} onChange={(userLimit) => updateWorkspaceForm({ userLimit })} />
          <TextField label="Team limit" type="number" min="1" max="100" value={workspaceForm.teamLimit} onChange={(teamLimit) => updateWorkspaceForm({ teamLimit })} />
        </SimpleForm>
      </Panel>
      <Panel title="Reset Trial User Password" subtitle="Platform operator only. Confirm the user's identity before resetting their password.">
        <SimpleForm submitLabel="Reset Password" submittingLabel="Resetting..." onSubmit={resetPassword}>
          <SelectField label="Trial workspace" value={resetForm.workspaceId} options={[{ value: "", label: "Select workspace" }, ...workspaces.filter((w) => w.id !== "demo" && w.loginMode === "password").map((w) => ({ value: w.id, label: `${w.companyName} (${w.slug})` }))]} onChange={(workspaceId) => { setResetMessage(""); setResetForm({ workspaceId, username: "", newPassword: "" }); }} />
          <TextField label="Exact username" value={resetForm.username} required maxLength={80} onChange={(username) => setResetForm({ ...resetForm, username })} />
          <TextField label="Temporary password" type="password" value={resetForm.newPassword} required maxLength={128} onChange={(newPassword) => setResetForm({ ...resetForm, newPassword })} />
        </SimpleForm>
        {resetMessage ? <div className="notice" role="status">{resetMessage}</div> : null}
      </Panel>
      <Panel title="Customer Trial Login Pages" subtitle="Share these links with customers. Each workspace sees only its own users, teams, prospects, and reports.">
        <ResourceTable
          rows={workspaces}
          columns={[
            ["companyName", "Customer"],
            ["slug", "Login URL", (_, row) => <a className="inline-link" href={workspaceLoginPath(row)}>{workspaceLoginPath(row)}</a>],
            ["status", "Status", formatWorkspaceStatus],
            ["loginMode", "Sign-in", formatLoginMode],
            ["trialEndDate", "Trial Ends", formatWorkspaceDate],
            ["planLimits", "Limits", formatWorkspaceLimits],
          ]}
        />
      </Panel>
    </section>
  );
}

function defaultWorkspaceForm() {
  return {
    companyName: "",
    slug: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    trialEndDate: defaultTrialEndDate(),
    loginMode: "password",
    userLimit: 10,
    teamLimit: 3,
  };
}

function formatLoginMode(value) {
  if (value === "password") return "Password";
  return value === "ldap" ? "Enterprise" : "Demo selection";
}

function workspaceLoginPath(workspace) {
  if (!workspace || workspace.slug === "demo") return "/";
  return workspace.loginPath || `/trial/${workspace.slug}`;
}

function formatWorkspaceStatus(value) {
  if (value === "active") return "Active";
  if (value === "expired") return "Expired";
  if (value === "cancelled") return "Cancelled";
  if (value === "trial") return "Trial";
  return value || "-";
}

function formatWorkspaceDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function formatWorkspaceLimits(value) {
  if (!value) return "-";
  const userLimit = value.users ?? "Unlimited";
  const teamLimit = value.teams ?? "Unlimited";
  return `${userLimit} users / ${teamLimit} teams`;
}

function defaultTrialEndDate() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date.toISOString().slice(0, 10);
}

function slugifyWorkspace(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
